import { Player } from '../types'
import { ChevronLeft, Video, BarChart3, GitCommitHorizontal, Award, User, Bookmark, BookmarkCheck, Share2, ClipboardCheck, Star, CheckCircle2, MapPin, Calendar, Shirt, Ruler, Weight, FilePlus, Pencil, Lock } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useAuthStore, useWatchlistStore } from '../stores'
import { useAchievementStore } from '../stores/useAchievementStore'
import { useProStatus } from '../utils/useProStatus'
import { notifyProfileViewed, notifyProfileSaved } from '../utils/notify'
import PlayerMatchClips from './PlayerMatchClips'
import PlayerStatistics from './PlayerStatistics'
import PlayerCareerTimeline from './PlayerCareerTimeline'
import PlayerAchievements from './PlayerAchievements'
import PlayerRating from './PlayerRating'
import ReportGenerator from './ReportGenerator'
import PositionPitchMap from './PositionPitchMap'
import ScoutaAnalystReport from './ScoutaAnalystReport'
import { calculateAge } from '../utils/age'

type TabType = 'overview' | 'clips' | 'statistics' | 'timeline' | 'achievements' | 'rating'

const TABS: { id: TabType; label: string; icon: React.ReactNode }[] = [
  { id: 'overview', label: 'Overview', icon: <User size={16} /> },
  { id: 'clips', label: 'Match Clips', icon: <Video size={16} /> },
  { id: 'statistics', label: 'Statistics', icon: <BarChart3 size={16} /> },
  { id: 'timeline', label: 'Career', icon: <GitCommitHorizontal size={16} /> },
  { id: 'achievements', label: 'Achievements', icon: <Award size={16} /> },
  { id: 'rating', label: 'Rating', icon: <Star size={16} /> },
]

interface PlayerDetailProps {
  player: Player
  onEdit?: () => void
  onBack: () => void
  canEdit?: boolean
  onMessagePlayer?: (playerId: string) => void
}

function PlayerDetail({ player, onBack, onEdit, onMessagePlayer }: PlayerDetailProps) {
  const { user } = useAuthStore()
  const { addToWatchlist, removeFromWatchlist, isInWatchlist } = useWatchlistStore()
  const { fetchAchievements } = useAchievementStore()
  const { isPro } = useProStatus()
  const [activeTab, setActiveTab] = useState<TabType>('clips')
  const [showReportGen, setShowReportGen] = useState(false)
  const [copied, setCopied] = useState(false)

  const isSaved = isInWatchlist(player.id)
  const isGuest = !user
  const canMessage = user && (user.role === 'scout' || user.role === 'club') && user.id !== player.id

  useEffect(() => {
    if (player.id) {
      fetchAchievements(player.id)
    }
  }, [player.id, fetchAchievements])

  // Notify when a scout/club views this profile
  useEffect(() => {
    if (user && (user.role === 'scout' || user.role === 'club' || user.role === 'admin') && player.id) {
      notifyProfileViewed(
        player.id,
        user.name,
        user.role as 'scout' | 'club' | 'admin',
        user.id
      )
    }
  }, [player.id])

  const handleShare = () => {
    const url = new URL(window.location.origin + window.location.pathname)
    url.hash = `#/player-profile/${player.id}`
    navigator.clipboard.writeText(url.toString())
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleToggleSave = () => {
    if (!user) return
    if (isSaved) {
      removeFromWatchlist(player.id)
    } else {
      addToWatchlist(player.id)
      if (user.role === 'scout' || user.role === 'club' || user.role === 'admin') {
        notifyProfileSaved(player.id, user.name, user.role as 'scout' | 'club' | 'admin', user.id)
      }
    }
  }

  const handleMessage = () => {
    if (onMessagePlayer) {
      onMessagePlayer(player.id)
    } else {
      window.sessionStorage.setItem('scoutang:message-player', player.id)
      window.dispatchEvent(new CustomEvent('scoutang:message-player', { detail: { playerId: player.id } }))
    }
  }

  if (showReportGen) {
    return <ReportGenerator player={player} onClose={() => setShowReportGen(false)} />
  }

  const renderOverview = () => (
    <div className="space-y-8 animate-in fade-in duration-300">
      {/* Hero Banner */}
      <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-6 sm:p-8">
        <div className="flex flex-col md:flex-row gap-8">
          <div className="w-full md:w-72 shrink-0">
            <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-gray-900 border-2 border-transparent hover:border-green-500/20 transition-all duration-500">
              <img
                src={player.photo_url || '/placeholder-user.png'}
                alt={player.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="flex-1 space-y-6">
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                {player.is_verified && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-500/10 text-green-400 text-[10px] font-black rounded-full border border-green-500/20 uppercase tracking-widest">
                    <CheckCircle2 size={12} />
                    Verified
                  </span>
                )}
                {player.is_top_talent && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-500/10 text-amber-400 text-[10px] font-black rounded-full border border-amber-500/20 uppercase tracking-widest">
                    <Award size={12} />
                    Top Talent
                  </span>
                )}
              </div>
              <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tighter leading-none italic uppercase">
                {player.name}
              </h1>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Position', value: player.main_position || 'N/A' },
                { label: 'Age', value: player.date_of_birth ? `${calculateAge(player.date_of_birth)} yrs` : `${player.age} yrs` },
                { label: 'Club', value: player.club || 'Free Agent' },
                { label: 'League', value: player.league || 'N/A' },
              ].map((item) => (
                <div key={item.label} className="bg-[#0d1117] rounded-2xl p-4 border border-gray-800">
                  <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-1">{item.label}</p>
                  <p className="text-white font-bold text-sm truncate">{item.value}</p>
                </div>
              ))}
            </div>

            {player.biography && (
              <div className="bg-white/5 p-5 rounded-2xl border border-gray-800">
                <p className="text-gray-300 text-sm leading-relaxed italic">{player.biography}</p>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Goals', value: player.goals || 0, color: 'text-green-400' },
                { label: 'Assists', value: player.assists || 0, color: 'text-blue-400' },
                { label: 'Matches', value: player.apps || 0, color: 'text-white' },
                { label: 'Minutes', value: player.mins || 0, color: 'text-white' },
              ].map((item) => (
                <div key={item.label} className="bg-[#161b22] border border-gray-800 rounded-2xl p-4 text-center">
                  <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-1">{item.label}</p>
                  <p className={`text-2xl font-black ${item.color}`}>{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {player.height && (
          <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
              <Ruler size={18} className="text-blue-400" />
            </div>
            <div>
              <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest">Height</p>
              <p className="text-white font-bold">{player.height} cm</p>
            </div>
          </div>
        )}
        {player.weight && (
          <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
              <Weight size={18} className="text-purple-400" />
            </div>
            <div>
              <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest">Weight</p>
              <p className="text-white font-bold">{player.weight} kg</p>
            </div>
          </div>
        )}
        {player.state && (
          <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center">
              <MapPin size={18} className="text-orange-400" />
            </div>
            <div>
              <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest">Location</p>
              <p className="text-white font-bold">{player.state}{player.lga ? `, ${player.lga}` : ''}</p>
            </div>
          </div>
        )}
        {player.foot && (
          <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
              <Shirt size={18} className="text-cyan-400" />
            </div>
            <div>
              <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest">Preferred Foot</p>
              <p className="text-white font-bold">{player.foot}</p>
            </div>
          </div>
        )}
        {player.date_of_birth && (
          <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-pink-500/10 flex items-center justify-center">
              <Calendar size={18} className="text-pink-400" />
            </div>
            <div>
              <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest">Date of Birth</p>
              <p className="text-white font-bold">{new Date(player.date_of_birth).toLocaleDateString()}</p>
              <p className="text-gray-400 text-xs mt-0.5">({calculateAge(player.date_of_birth)} years old)</p>
            </div>
          </div>
        )}
        {player.positions && player.positions.length > 1 && (
          <div className="bg-[#161b22] border border-gray-800 rounded-2xl p-5">
            <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-2">Positions</p>
            <div className="flex flex-wrap gap-2">
              {player.positions.map(p => (
                <span key={p} className="px-3 py-1 bg-white/5 rounded-lg text-[10px] font-black uppercase border border-gray-800 text-gray-300">
                  {p}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Agent / Manager — Scout & Club only */}
      {!isGuest && (user?.role === 'scout' || user?.role === 'club' || user?.role === 'admin') && (
        <div className={`rounded-3xl p-6 border ${
          player.has_agent && player.agent_name
            ? 'bg-[#161b22] border-green-500/20'
            : 'bg-red-900/5 border-red-500/20'
        }`}>
          <div className="flex items-center gap-2 mb-4">
            <User size={18} className={player.has_agent && player.agent_name ? 'text-green-400' : 'text-red-400'} />
            <h3 className="text-xs font-black text-white uppercase tracking-widest">Agent / Manager</h3>
            {!player.has_agent && (
              <span className="ml-auto px-3 py-1 bg-red-500/10 border border-red-500/20 text-red-400 text-[10px] font-black uppercase tracking-widest rounded-full">
                NO AGENT
              </span>
            )}
          </div>
          {player.has_agent && player.agent_name ? (
            isPro ? (
              <div className="space-y-3">
                <div className="bg-[#0d1117] rounded-2xl p-4 border border-gray-800">
                  <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-1">Agent Name</p>
                  <p className="text-white font-bold">{player.agent_name}</p>
                </div>
                {player.agent_contact && (
                  <div className="bg-[#0d1117] rounded-2xl p-4 border border-gray-800">
                    <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-1">Contact</p>
                    <p className="text-green-400 font-bold">{player.agent_contact}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-[#0d1117] border border-dashed border-yellow-500/20 rounded-2xl p-6 text-center">
                <Lock size={20} className="text-yellow-400 mx-auto mb-2" />
                <p className="text-yellow-400 text-xs font-black uppercase tracking-widest">Agent details available for Pro scouts</p>
                <a href="/pricing" className="mt-3 inline-block px-4 py-1.5 bg-green-500 text-black text-[10px] font-bold rounded-lg hover:bg-green-400 transition uppercase">Upgrade</a>
              </div>
            )
          ) : (
            <div className="bg-red-900/5 border border-dashed border-red-500/20 rounded-2xl p-6 text-center">
              <p className="text-red-400 text-xs font-black uppercase tracking-widest">No Agent / Manager</p>
              <p className="text-gray-500 text-[10px] mt-1 uppercase tracking-widest">This player has indicated they have no representative</p>
            </div>
          )}
        </div>
      )}

      {/* Position Map */}
      <PositionPitchMap player={player} />

      {/* Scouta Analyst Report */}
      <ScoutaAnalystReport player={player} />

      {/* Scouting Report */}
      {!isGuest && (
        <div className="bg-[#161b22] border border-gray-800 rounded-3xl p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-2">
              <FilePlus className="text-indigo-400" size={18} />
              <h3 className="text-lg font-bold text-white">Scouting Analysis</h3>
            </div>
            <button
              onClick={() => setShowReportGen(true)}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition"
            >
              {player.scout_report ? 'Edit Report' : 'Draft Report'}
            </button>
          </div>
          {player.scout_report ? (
            <div className="bg-white/5 p-5 rounded-2xl border border-gray-800 italic text-gray-300 whitespace-pre-wrap text-sm leading-relaxed">
              {player.scout_report}
            </div>
          ) : (
            <div className="text-center py-8 border-2 border-dashed border-gray-800 rounded-2xl">
              <p className="text-gray-500 text-xs font-black uppercase tracking-widest">No report yet</p>
            </div>
          )}
        </div>
      )}
    </div>
  )

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview()
      case 'clips':
        return <PlayerMatchClips player={player} />
      case 'statistics':
        return <PlayerStatistics player={player} />
      case 'timeline':
        return <PlayerCareerTimeline player={player} />
      case 'achievements':
        return <PlayerAchievements player={player} />
      case 'rating':
        return <PlayerRating player={player} />
      default:
        return renderOverview()
    }
  }

  return (
    <div className="min-h-screen bg-[#050910]">
      <header className="border-b border-gray-800 bg-gray-950/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <button
                onClick={onBack}
                className="flex items-center gap-2 text-green-400 hover:text-green-300 transition text-xs font-black uppercase tracking-widest"
              >
                <ChevronLeft size={18} />
                <span className="hidden sm:inline">Back</span>
              </button>
              <div className="h-6 w-px bg-gray-800" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-green-500/20 flex items-center justify-center overflow-hidden">
                  {player.photo_url ? (
                    <img src={player.photo_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User size={16} className="text-green-400" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-bold text-white leading-none">{player.name}</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-widest mt-0.5">{player.main_position} • {player.club || 'Free Agent'}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {onEdit && (user?.role === 'admin' || user?.role === 'club' || user?.id === player.scout || user?.email?.toLowerCase() === player.scout?.toLowerCase()) && (
                <button
                  onClick={onEdit}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500 hover:bg-green-400 text-black rounded-xl text-xs font-black uppercase tracking-widest transition"
                >
                  <Pencil size={14} />
                  <span className="hidden sm:inline">Edit</span>
                </button>
              )}
              <button
                onClick={handleShare}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-widest transition border ${
                  copied ? 'bg-green-900/20 border-green-700 text-green-400' : 'bg-gray-900 border-gray-800 text-gray-400 hover:text-white hover:border-gray-600'
                }`}
              >
                {copied ? <ClipboardCheck size={14} /> : <Share2 size={14} />}
                <span className="hidden sm:inline">{copied ? 'Copied!' : 'Share'}</span>
              </button>
              {user && (
                <button
                  onClick={handleToggleSave}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-widest transition border ${
                    isSaved ? 'bg-green-900/20 border-green-700 text-green-400' : 'bg-gray-900 border-gray-800 text-gray-400 hover:text-white hover:border-gray-600'
                  }`}
                >
                  {isSaved ? <BookmarkCheck size={14} /> : <Bookmark size={14} />}
                  <span className="hidden sm:inline">{isSaved ? 'Saved' : 'Save'}</span>
                </button>
              )}
              {canMessage && (
                <button
                  onClick={handleMessage}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500 hover:bg-green-400 text-black rounded-xl text-xs font-black uppercase tracking-widest transition"
                >
                  Message
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 flex gap-1 px-4 py-0 bg-gray-950 overflow-x-auto whitespace-nowrap hide-scrollbar">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 font-medium text-sm transition border-b-2 whitespace-nowrap flex-shrink-0 flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'border-green-500 text-green-400'
                  : 'border-transparent text-gray-400 hover:text-gray-200'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {renderTabContent()}
      </main>
    </div>
  )
}

export default PlayerDetail
